import * as React from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { CopyTooltip } from '~/sotano-kit/primitives/copy-tooltip'
import { apiOrigin } from '~/constants/routing'
import { handleCopyPkCloak, pkCloak } from './pk-cloak'
import { ReRollAPITokenForm } from './re-roll-api-token/form'
import { Repository, repositorySchema } from '@basehub/data/replicache/index'
import {
  PublicRepo,
  isUserRepo,
} from '@basehub/data/replicache/mutators/_public-types'
import { Label } from '~/sotano-kit/primitives/input'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { McpTokenDisplay } from './mcp-token-display'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'

export const AboutTheAPI = ({ repo }: { repo: Repository | PublicRepo }) => {
  const parsedRepo = repositorySchema.safeParse(repo)
  const readToken = parsedRepo.success
    ? parsedRepo.data.tokens.find((t) => t.scope === 'read')
    : null
  const adminToken = parsedRepo.success
    ? parsedRepo.data.tokens.find((t) => t.scope === 'write')
    : null

  const teamSlug = useSelectedTeam().team?.slug

  return (
    <div className="bg-dark-base-dark rounded-lg border border-dark-border-solid max-h-max sticky top-8 w-[320px]">
      <div className="flex flex-col">
        <div className="flex-1 border-b border-dark-border-solid last-of-type:border-none py-3 px-4">
          <p className="flex items-center">
            <Label
              size="sm"
              className="mr-1.5 !p-0 [&>span]:!pb-0.5"
              label="API Endpoint"
              blocked={false}
            />
          </p>
          <p className="text-xs text-dark-dim gap-x-0.5">
            {`${apiOrigin}/graphql`}

            <CopyTooltip
              className="inline align-middle ml-2 text-dark-muted"
              iconClassName="!h-3.5 !w-3.5 align-middle"
              copyContent={`${apiOrigin}/graphql`}
            />
          </p>
        </div>

        {isUserRepo(repo) ? (
          <>
            <Token
              label="Read Token"
              token={readToken?.token ?? ''}
              description="Can read repo content."
            />
            <Token
              label="Admin Token"
              token={adminToken?.token ?? ''}
              description="Can read and write repo content. Use carefully."
            />
            <MCPConnect repo={repo} teamSlug={teamSlug} />
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  )
}

const MCPConnect = ({
  repo,
  teamSlug,
}: {
  repo: Repository | PublicRepo
  teamSlug: string | undefined
}) => {
  const [integration, setIntegration] = React.useState<'cursor' | 'other'>(
    'cursor'
  )

  return (
    <div className="flex-1 border-b border-dark-border-solid last-of-type:border-none py-3 pl-4 pr-3">
      <div className="flex items-center text-sm mb-2 justify-between">
        <Label
          size="sm"
          className="mr-1.5 !p-0 [&>span]:!pb-0.5"
          label="MCP Integration"
          blocked={false}
        />
        <Select
          required
          name="mcp-integration"
          value={integration}
          onValueChange={(v) => setIntegration(v as 'cursor' | 'other')}
        >
          <SelectTrigger
            id="mcp-integration"
            aria-label="Select a mcp integration"
          >
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem key="cursor" value="cursor">
              Cursor
            </SelectItem>
            <SelectItem key="claude-code" value="claude-code">
              Claude Code
            </SelectItem>
            <SelectItem key="other" value="other">
              Other
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <McpTokenDisplay
        repoId={repo.id}
        repoSlug={repo.slug}
        teamSlug={teamSlug}
        integration={integration}
      />

      <p className="text-xs text-pretty text-dark-faint max-w-[250px] mt-1.5">
        MCP tools have read and write access to your BaseHub repository.{' '}
        <ButtonLink
          href="https://modelcontextprotocol.io/introduction"
          target="_blank"
          rel="noreferrer"
          unstyled
          focusStyled
          className="text-dark-dim hover:underline"
        >
          Learn more.
        </ButtonLink>
      </p>
    </div>
  )
}

const Token = ({
  label,
  token,
  description,
}: {
  label: string
  token: string
  description: string
}) => {
  const isMemberOrHigher = useIsMemberOrHigher()

  return (
    <div className="flex-1 border-b border-dark-border-solid last-of-type:border-none py-3 pl-4 pr-3">
      <div className="flex items-center text-sm">
        <Label
          size="sm"
          className="mr-1.5 !p-0 [&>span]:!pb-0.5"
          label={label}
          blocked={false}
        />
        {isMemberOrHigher && (
          <div className="ml-auto">
            <ReRollAPITokenForm
              token={token}
              tokenName={label.replace('Token', '').trim()}
            />
          </div>
        )}
      </div>
      <p
        className="text-xs text-dark-dim"
        onCopy={(e) => handleCopyPkCloak(e, token)}
      >
        {pkCloak}

        <CopyTooltip
          className="inline align-middle ml-2 text-dark-muted"
          iconClassName="!h-3.5 !w-3.5 align-middle"
          copyContent={token}
        />
      </p>
      <p className="text-xs text-pretty text-dark-faint max-w-[250px] mt-0.5">
        {description}
      </p>
    </div>
  )
}
